import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Droplets, Wind, Send, Sofa, Bed, Package, Tag, CalendarPlus as CalendarIcon, Clock, MapPin, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import CleaningPricingTable from '@/components/CleaningPricingTable';
import { cleaningPricing } from '@/data/cleaningPricing';
import { useContent } from '@/contexts/ContentContext';

// Zones and Promo Packages removed as per new requirements

const FuturaCleaningTab = () => {
    const { toast } = useToast();
    const { content } = useContent();
    const [booking, setBooking] = useState({
        service: '',
        quantity: 1,
        distance: 0,
        address: '',
        date: '',
        timeSlot: '',
        name: '',
        email: '',
        phone: '',
        coupon: ''
    });
    const [discount, setDiscount] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const allServices = useMemo(() => [
        ...cleaningPricing.furniture,
        ...cleaningPricing.mattresses,
        ...cleaningPricing.packages,
    ], []);

    const baseQuote = useMemo(() => {
        const service = allServices.find(s => s.item === booking.service);
        if (!service) return 0;
        const priceString = service.price.split('-')[0].replace('$', '').trim();
        const price = parseFloat(priceString);
        if (isNaN(price)) return 0;
        if (cleaningPricing.packages.some(p => p.item === service.item)) {
            return price;
        }
        return price * booking.quantity;
    }, [booking.service, booking.quantity, allServices]);

    const transportFee = useMemo(() => {
        const baseFee = content?.cleaning?.transportBaseFee || 5;
        const pricePerKm = content?.cleaning?.transportPricePerKm || 0.5;

        if (booking.distance <= 0) return 0;
        return baseFee + (booking.distance * pricePerKm);
    }, [booking.distance, content]);

    const totalQuote = useMemo(() => {
        const subtotal = baseQuote + transportFee;
        return subtotal * (1 - discount);
    }, [baseQuote, transportFee, discount]);

    const handleCouponApply = () => {
        if (booking.coupon.toUpperCase() === 'FUTURA10') {
            setDiscount(0.10);
            toast({ title: '✅ Cupón Aplicado', description: 'Se ha aplicado un 10% de descuento.' });
        } else {
            setDiscount(0);
            toast({ title: '❌ Cupón Inválido', description: 'El código de cupón no es válido.', variant: 'destructive' });
        }
    };

    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        if (baseQuote === 0 || !booking.name || !booking.phone || !booking.email) {
            toast({ title: "⚠️ Formulario Incompleto", description: "Por favor, completa todos los campos requeridos.", variant: "destructive" });
            return;
        }
        setIsSubmitting(true);

        // Calculate totals
        const subtotalVal = baseQuote;
        const transportVal = transportFee;
        const discountVal = (subtotalVal + transportVal) * discount;
        const totalVal = (subtotalVal + transportVal) - discountVal;

        // Construct WhatsApp Message
        const message = `*✨ Nueva Solicitud de Cotización - Futura Cleaning*

*👤 Cliente:* ${booking.name}
*📧 Email:* ${booking.email}
*📞 Teléfono:* ${booking.phone}
*📍 Dirección:* ${booking.address}
*🚗 Distancia:* ${booking.distance} km

*📋 Detalle del Pedido:*
- Servicio: ${booking.service}
- Cantidad: ${booking.quantity}

*💰 Desglose:*
- Subtotal: $${subtotalVal.toFixed(2)}
- Transporte: $${transportVal.toFixed(2)}
${discount > 0 ? `- Descuento: -$${discountVal.toFixed(2)}\n` : ''}
*💵 Total Estimado: $${totalVal.toFixed(2)}*

${booking.coupon ? `*🎟️ Cupón:* ${booking.coupon}` : ''}

_Solicitud enviada desde el sitio web._`;

        // WhatsApp URL
        const phoneNumber = "50360272418";
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

        window.open(whatsappUrl, '_blank');

        toast({
            title: "✅ Abriendo WhatsApp",
            description: "El mensaje está listo para enviar.",
        });
        setIsSubmitting(false);
    };

    return (
        <div className="bg-gray-50/50 rounded-2xl pb-16">
            <section className="relative w-full aspect-video md:aspect-[2.4/1] rounded-t-2xl overflow-hidden mb-12">
                <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/z4A8-dwbO7k?autoplay=1&mute=1&loop=1&playlist=z4A8-dwbO7k&controls=0&showinfo=0"
                    title="Futura Cleaning Welcome Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                ></iframe>
                <div className="absolute inset-0 bg-secondary/70 flex items-center justify-center text-center p-4">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-3 drop-shadow-lg">Tu Mueble No Está Viejo, ¡Está Sucio!</h2>
                        <p className="text-lg text-gray-200 max-w-2xl mx-auto drop-shadow-md">Descubre cómo nuestro sistema de inyección-succión revive tus muebles.</p>
                    </motion.div>
                </div>
            </section>

            <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-3">

                        {/* Benefits & Times Section */}
                        <motion.section initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="mb-12 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h3 className="text-2xl font-bold text-secondary mb-6 flex items-center gap-2">
                                <Sparkles className="w-6 h-6 text-primary" />
                                ¿Por qué elegirnos?
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-lg text-gray-800">Beneficios del Sistema</h4>
                                    <ul className="space-y-2 text-gray-600">
                                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 shrink-0" /> Eliminación de ácaros y bacterias</li>
                                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 shrink-0" /> Restauración de colores vivos</li>
                                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 shrink-0" /> Productos biodegradables</li>
                                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 shrink-0" /> Sin residuos jabonosos</li>
                                    </ul>
                                </div>
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-lg text-gray-800">Tiempos Estimados</h4>
                                    <ul className="space-y-3 text-sm text-gray-600">
                                        <li className="flex items-start gap-2">
                                            <Clock className="w-4 h-4 text-blue-500 mt-0.5" />
                                            <span><strong>Por colchón:</strong> 1.5 a 2 horas.</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Wind className="w-4 h-4 text-blue-500 mt-0.5" />
                                            <span><strong>Secado:</strong> Alrededor de 4 horas (varía según la hora del día).</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Sofa className="w-4 h-4 text-blue-500 mt-0.5" />
                                            <span><strong>Juego de sala 3-2-1:</strong> Aproximadamente 4 horas.</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Bed className="w-4 h-4 text-blue-500 mt-0.5" />
                                            <span><strong>4 colchones (2K + 2M):</strong> Alrededor de 7 horas.</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </motion.section>

                        <motion.section initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="mb-12">
                            <h3 className="text-2xl font-bold text-secondary mb-6">Catálogo Completo de Servicios</h3>
                            <div className="space-y-8">
                                <CleaningPricingTable title="Limpieza de Muebles" data={cleaningPricing.furniture} icon={Sofa} />
                                <CleaningPricingTable title="Limpieza de Colchones" data={cleaningPricing.mattresses} icon={Bed} />
                                <CleaningPricingTable title="Paquetes de Sala" data={cleaningPricing.packages} icon={Package} />
                            </div>
                        </motion.section>

                        <motion.section initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                            <h3 className="text-2xl font-bold text-secondary mb-6">Galería Antes y Después</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <img className="rounded-xl shadow-lg aspect-square object-cover" alt="Sofá sucio antes de la limpieza" src="https://images.unsplash.com/photo-1672912280009-2bcb742c0313" />
                                <img className="rounded-xl shadow-lg aspect-square object-cover" alt="Sofá limpio después del servicio" src="https://images.unsplash.com/photo-1672912280009-2bcb742c0313" />
                                <img className="rounded-xl shadow-lg aspect-square object-cover" alt="Alfombra manchada" src="https://images.unsplash.com/photo-1694796820129-b70a675ec6a1" />
                                <img className="rounded-xl shadow-lg aspect-square object-cover" alt="Alfombra impecable" src="https://images.unsplash.com/photo-1673227615307-e269db5c0f56" />
                            </div>
                        </motion.section>
                    </div>

                    <div className="lg:col-span-2">
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white p-6 md:p-8 rounded-2xl shadow-2xl sticky top-28">
                            <h3 className="text-2xl font-bold text-center text-secondary mb-6">¡Cotiza y Agenda Tu Limpieza!</h3>
                            <form onSubmit={handleBookingSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <Label>Servicio</Label>
                                        <Select onValueChange={value => setBooking(b => ({ ...b, service: value, quantity: 1 }))} value={booking.service}>
                                            <SelectTrigger><SelectValue placeholder="Selecciona un servicio" /></SelectTrigger>
                                            <SelectContent>{allServices.map(s => <SelectItem key={s.item} value={s.item}>{s.item}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    {booking.service && !cleaningPricing.packages.some(p => p.item === booking.service) && (
                                        <div className="col-span-2">
                                            <Label htmlFor="quantity">Cantidad</Label>
                                            <Input id="quantity" type="number" min="1" value={booking.quantity} onChange={e => setBooking(b => ({ ...b, quantity: Number(e.target.value) }))} />
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <Label htmlFor="distance">Distancia (km)</Label>
                                    <div className="relative">
                                        <Input
                                            id="distance"
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            value={booking.distance}
                                            onChange={e => setBooking(b => ({ ...b, distance: Number(e.target.value) }))}
                                            placeholder="0"
                                        />
                                        <span className="absolute right-3 top-2.5 text-gray-400 text-sm">km</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Desde el centro de Santa Ana</p>
                                </div>

                                <div>
                                    <Label htmlFor="address">Dirección Completa</Label>
                                    <Input id="address" value={booking.address} onChange={e => setBooking(b => ({ ...b, address: e.target.value }))} placeholder="Calle, # de casa, colonia..." required />
                                </div>

                                <div className="pt-4 border-t">
                                    <Label htmlFor="name">Nombre Completo</Label>
                                    <Input id="name" value={booking.name} onChange={e => setBooking(b => ({ ...b, name: e.target.value }))} placeholder="Tu nombre" required />
                                </div>
                                <div>
                                    <Label htmlFor="email">Correo Electrónico</Label>
                                    <Input id="email" type="email" value={booking.email} onChange={e => setBooking(b => ({ ...b, email: e.target.value }))} placeholder="tu@email.com" required />
                                </div>
                                <div>
                                    <Label htmlFor="phone">Teléfono de Contacto</Label>
                                    <Input id="phone" type="tel" value={booking.phone} onChange={e => setBooking(b => ({ ...b, phone: e.target.value }))} placeholder="7777-8888" required />
                                </div>

                                <div className="pt-4 border-t">
                                    <Label htmlFor="coupon">Código de Cupón</Label>
                                    <div className="flex gap-2">
                                        <Input id="coupon" value={booking.coupon} onChange={e => setBooking(b => ({ ...b, coupon: e.target.value }))} placeholder="Ej: FUTURA10" />
                                        <Button type="button" variant="outline" onClick={handleCouponApply}>Aplicar</Button>
                                    </div>
                                </div>

                                {(baseQuote > 0 || transportFee > 0) && (
                                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center bg-gray-100 p-4 rounded-xl space-y-2">
                                        <div className="flex justify-between text-sm"><span>Subtotal:</span><span>${baseQuote.toFixed(2)}</span></div>
                                        <div className="flex justify-between text-sm"><span>Transporte:</span><span>+ ${transportFee.toFixed(2)}</span></div>
                                        {discount > 0 && <div className="flex justify-between text-sm text-green-600"><span>Descuento ({discount * 100}%):</span><span>- ${((baseQuote + transportFee) * discount).toFixed(2)}</span></div>}
                                        <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2"><span>Total Estimado:</span><span className="text-primary">${totalQuote.toFixed(2)}</span></div>
                                    </motion.div>
                                )}

                                <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                                    {isSubmitting ? 'Enviando...' : <><Send className="mr-2 h-5 w-5" /> Enviar Cotización</>}
                                </Button>
                                <p className="text-xs text-center text-gray-500">Nos pondremos en contacto para confirmar la cita.</p>
                            </form>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FuturaCleaningTab;
